import { httpClient } from '../../lib/utils';
import { envValue } from '../../config/envUtils';
import { SAVE_LEVEL } from '../constants/Level';
import { Service } from './Service';
import { themeSystemDefault, sbemShadCNThemeDefault } from '../constants/SystemDefaults';
import store from '../store/Store';
import * as ActionTypes from '../constants/ActionTypes';
import moment from 'moment';
import { merge, defaultsDeep, assign, get, find, omit } from 'lodash';
import { filterThemeUniqueGlobal } from '../constants/uniqueKeyToGlobalMap';
import { CALCULATE_FONT_USAGE } from '../constants/ActionTypes';
import { postResponseToSplunkFailed, postResponseToSplunkSuccess } from './logging/splunklogging';

const SOURCEURL = envValue('SOURCEURL');
const FRONTMAN_CHANNEL_URL = envValue('FRONTMAN_CHANNEL_URL');
const SBEM_CLEAR_CACHE_URL = envValue('SBEM_CLEAR_CACHE_URL');

function newOrUpdate(mode = 'new', chainId, hotelId, themeCode, content, saveLevel = SAVE_LEVEL.HOTEL, category = 'SBE') {
    const categoryParam = category === 'SBEM' ? `&category=SBEM` : '&category=SBE';
    return httpClient[mode === 'new' ? 'post' : 'put'](
        `${SOURCEURL}/shs-bedesigner-services/v1/contents/theme?${ThemeService.paramProxy(
            chainId,
            hotelId,
            null,
            saveLevel
        )}&contentcode=${themeCode}&clearcache=all${categoryParam}`,
        content
    );
}

export function checkIfThemeExists(params) {
    const chainId = get(params, 'chainId');
    const hotelId = get(params, 'hotelId');
    const shellCode = get(params, 'shellCode');
    return httpClient
        .get(`${FRONTMAN_CHANNEL_URL}/shell?chainId=${chainId}&hotelId=${hotelId}&shellCode=${shellCode}`)
        .then(response => {
            const shells = get(response, 'data.shells', []);
            const foundShell = find(shells, item => {
                return get(item, 'code') === shellCode;
            });
            return foundShell;
        });
}

export function clearSbemThemeCache(chainId, hotelId, themeCode, saveLevel = SAVE_LEVEL.HOTEL) {
    return httpClient.post(SBEM_CLEAR_CACHE_URL, { chainId, hotelId, themeCode, saveLevel, keyType: 'theme' });
}

export function saveThemeCodeToFrontman({ code, name, isDefault = false, chainId, hotelId, saveLevel }) {
    const payload = {};
    const shell = { code, isDefault };
    const chain = {};
    const hotel = {};
    const level = !!saveLevel ? saveLevel : SAVE_LEVEL.HOTEL;
    const requestTime = moment().valueOf();

    if (chainId && level === SAVE_LEVEL.CHAIN) {
        assign(chain, { id: chainId });
        assign(shell, { chain });
    }
    if (hotelId && level === SAVE_LEVEL.HOTEL) {
        assign(hotel, { id: hotelId });
        assign(shell, { hotel });
    }
    if (name) {
        assign(shell, { name });
    }
    assign(payload, { shells: [shell] });
    store.dispatch({ type: ActionTypes.SHELL_SAVE_REQUEST });
    return httpClient
        .post(`${FRONTMAN_CHANNEL_URL}/shell`, payload)
        .then(response => {
            store.dispatch({ type: ActionTypes.SHELL_SAVE_SUCCESS });
            postResponseToSplunkSuccess(
                response.config.method,
                response.config.url,
                (moment().valueOf() - requestTime) / 1000 + 'sec',
                response.status
            );
        })
        .catch(error => {
            const response = error.response;
            store.dispatch({ type: ActionTypes.SHELL_SAVE_FAILURE });
            postResponseToSplunkFailed(
                response.config.method,
                response.config.url,
                (moment().valueOf() - requestTime) / 1000 + 'sec',
                error.message
            );
        });
}

export class ThemeService extends Service {
    /**
     * Gets list of themes at given save level
     * @param chainId
     * @param hotelId
     * @param saveLevel
     * @param category - Optional category filter (e.g., 'SBEM')
     * @returns {Promise}
     */
    static list(chainId, hotelId, saveLevel = SAVE_LEVEL.HOTEL, category = 'SBE') {
        const categoryParam = category === 'SBEM' ? `&category=SBEM` : '&category=SBE';
        return httpClient.get(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/theme/list?${ThemeService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}${categoryParam}`
        );
    }

    /**
     * Gets list of SBEM themes at given save level
     * @param chainId
     * @param hotelId
     * @param saveLevel
     * @returns {Promise}
     */
    static listSbem(chainId, hotelId, saveLevel = SAVE_LEVEL.HOTEL) {
        return ThemeService.list(chainId, hotelId, saveLevel, 'SBEM');
    }

    /**
     * Gets single theme config at given save level
     * @param chainId
     * @param hotelId
     * @param contentCode
     * @param saveLevel
     * @param category - Optional category filter (e.g., 'SBEM')
     * @returns {Promise}
     */
    static get(chainId, hotelId, contentCode, saveLevel = SAVE_LEVEL.HOTEL, category = 'SBE') {
        const categoryParam = category === 'SBEM' ? `&category=SBEM` : '&category=SBE';
        return httpClient
            .get(
                `${SOURCEURL}/shs-bedesigner-services/v1/contents/theme?${ThemeService.paramProxy(
                    chainId,
                    hotelId,
                    null,
                    saveLevel
                )}&contentcode=${contentCode}${categoryParam}`,
                {}
            )
            .then(response => {
                if (category === 'SBEM') {
                    // For SBEM themes, return clean data without SBE processing
                    return response;
                } else {
                    // For SBE themes, apply the original processing
                    // cleans obsolete theme keys.  for now, only keys with Roboto font.  more keys can be added later.
                    const cleanedTheme = omit(response.data.BEDesignerResult.Value.Data, [
                        'header.heading',
                        'header.link.font',
                        'header.primaryText.font',
                        'header.subheading.font'
                    ]);
                    response.data.BEDesignerResult.Value.Data = defaultsDeep(
                        filterThemeUniqueGlobal(cleanedTheme),
                        themeSystemDefault
                    );
                    return response;
                }
            });
    }

    static delete(chainId, hotelId, contentCode, saveLevel = SAVE_LEVEL.HOTEL, category = 'SBE') {
        const categoryParam = category === 'SBEM' ? `&category=SBEM` : '&category=SBE';
        return httpClient.delete(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/theme?${ThemeService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}&contentcode=${contentCode}${categoryParam}`,
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );
    }
    /**
     * Get systemdefault themes
     */
    static systemdefault() {
        return Promise.resolve(themeSystemDefault);
    }

    /**
     * Get SBEM systemdefault theme
     */
    static systemdefaultSbem() {
        return Promise.resolve(sbemShadCNThemeDefault);
    }

    /**
     * Get history of all changes made to theme
     * @param chainId
     * @param hotelId
     * @param contentCode
     * @param saveLevel
     * @param category - Optional category filter (e.g., 'SBEM')
     */
    static all(chainId, hotelId, contentCode, saveLevel = SAVE_LEVEL.HOTEL, category = 'SBE') {
        const categoryParam = category === 'SBEM' ? `&category=SBEM` : '&category=SBE';
        return httpClient.get(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/theme/all?${ThemeService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}&contentcode=${contentCode}${categoryParam}`
        );
    }
    static setAsDefault(chainId, hotelId, contentCode, saveLevel = SAVE_LEVEL.HOTEL, category = 'SBE') {
        const categoryParam = category === 'SBEM' ? `&category=SBEM` : '&category=SBE';
        return httpClient.put(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/theme/default?${ThemeService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}&contentcode=${contentCode}${categoryParam}`,
            {}
        );
    }
    static setActive(chainId, hotelId, contentCode, value, saveLevel = SAVE_LEVEL.HOTEL, category = 'SBE') {
        const categoryParam = category === 'SBEM' ? `&category=SBEM` : '&category=SBE';
        return httpClient.put(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/theme/active?${ThemeService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}&contentcode=${contentCode}&active=${value}${categoryParam}`,
            {}
        );
    }

    /**
     * Helper function to fetch and combine both SBE and SBEM themes
     * @param chainId
     * @param hotelId
     * @param saveLevel
     * @returns {Promise} Combined theme list with isSbemTheme flag
     */
    static listCombined(chainId, hotelId, saveLevel = SAVE_LEVEL.HOTEL) {
        const sbePromise = ThemeService.list(chainId, hotelId, saveLevel).catch(() => ({ data: { Result: { ResultCode: 'FAILED' } } }));
        const sbemPromise = ThemeService.listSbem(chainId, hotelId, saveLevel).catch(() => ({ data: { Result: { ResultCode: 'FAILED' } } }));

        return Promise.all([sbePromise, sbemPromise]).then(([sbeResponse, sbemResponse]) => {
            let combinedResponse = {
                data: {
                    Result: { ResultCode: 'SUCCESS' },
                    BEDesignerResult: {
                        Value: {
                            ContentCodes: [],
                            DefaultContentCode: null
                        }
                    }
                }
            };

            // Process SBE themes
            if (sbeResponse.data.Result.ResultCode === 'SUCCESS') {
                const sbeContentCodes = sbeResponse.data.BEDesignerResult.Value.ContentCodes.map(theme => ({
                    ...theme,
                    isSbemTheme: false
                }));
                combinedResponse.data.BEDesignerResult.Value.ContentCodes = sbeContentCodes;
                combinedResponse.data.BEDesignerResult.Value.DefaultContentCode = sbeResponse.data.BEDesignerResult.Value.DefaultContentCode;
            }

            // Process SBEM themes - Use ContentId to determine if it's SBEM theme
            if (sbemResponse.data.Result.ResultCode === 'SUCCESS') {
                const sbemContentId = sbemResponse.data.BEDesignerResult.ContentId;
                const isSbemTheme = sbemContentId && sbemContentId.endsWith(':SBEM');

                const sbemContentCodes = sbemResponse.data.BEDesignerResult.Value.ContentCodes.map(theme => ({
                    ...theme,
                    isSbemTheme: isSbemTheme
                }));
                combinedResponse.data.BEDesignerResult.Value.ContentCodes = [
                    ...combinedResponse.data.BEDesignerResult.Value.ContentCodes,
                    ...sbemContentCodes
                ];
                
                combinedResponse.data.BEDesignerResult.Value.DefaultContentCodeSbem = sbemResponse.data.BEDesignerResult.Value.DefaultContentCode;
            }

            return combinedResponse;
        });
    }

    /**
     * Helper function for reloading data on certain SAVE_LEVEL
     */
    static reload(chainId, hotelId, saveLevel = SAVE_LEVEL.HOTEL, swtichToCode, dontMarkAsDefault, category = 'SBE') {
        let defaultContent = {
            level: SAVE_LEVEL.SYSTEM,
            code: 'systemdefault'
        };
        let defaultContentSbem = {
            level: SAVE_LEVEL.SYSTEM,
            code: 'systemdefault-sbem'
        };
        
        let mainPromise = Promise.resolve([
            {
                ContentCode: 'systemdefault',
                level: SAVE_LEVEL.SYSTEM,
                IsActive: true,
                isSbemTheme: false,
                MetaData: {
                    Timestamp: moment.utc().valueOf(),
                    Comment: 'systemdefault SBE'
                }
            },
            {
                ContentCode: 'systemdefault-sbem',
                level: SAVE_LEVEL.SYSTEM,
                IsActive: true,
                isSbemTheme: true,
                MetaData: {
                    Timestamp: moment.utc().valueOf(),
                    Comment: 'systemdefault SBEM'
                }
            }
        ]);
        /**
         * Gets list from chain level, or fail gracefully
         * @type {Promise.<TResult>}
         */
        mainPromise = mainPromise.then(upperLevelArray => {
            return ThemeService.listCombined(chainId, hotelId, SAVE_LEVEL.CHAIN).then(
                response => {
                    if (response.data.Result.ResultCode === 'SUCCESS') {
                        if (response.data.BEDesignerResult.Value.DefaultContentCode) {
                            defaultContent = {
                                level: SAVE_LEVEL.CHAIN,
                                code: response.data.BEDesignerResult.Value.DefaultContentCode
                            };
                        }
                        if (response.data.BEDesignerResult.Value.DefaultContentCodeSbem) {
                            defaultContentSbem = {
                                level: SAVE_LEVEL.CHAIN,
                                code: response.data.BEDesignerResult.Value.DefaultContentCodeSbem
                            };
                        }
                        return [
                            ...upperLevelArray,
                            ...response.data.BEDesignerResult.Value.ContentCodes.map(elem => {
                                elem['level'] = SAVE_LEVEL.CHAIN;
                                return elem;
                            })
                        ];
                    }
                },
                () => upperLevelArray
            );
        });
        /**
         * Gets list from save level, or fail gracefully
         * @type {Promise.<TResult>}
         */
        if (saveLevel !== SAVE_LEVEL.CHAIN) {
            mainPromise = mainPromise.then(upperLevelArray => {
                return ThemeService.listCombined(chainId, hotelId, saveLevel).then(
                    response => {
                        if (response.data.Result.ResultCode === 'SUCCESS') {
                            if (response.data.BEDesignerResult.Value.DefaultContentCode) {
                                defaultContent = {
                                    level: saveLevel,
                                    code: response.data.BEDesignerResult.Value.DefaultContentCode
                                };
                            }
                            if (response.data.BEDesignerResult.Value.DefaultContentCodeSbem) {
                                defaultContentSbem = {
                                    level: saveLevel,
                                    code: response.data.BEDesignerResult.Value.DefaultContentCodeSbem
                                };
                            }
                            return [
                                ...upperLevelArray,
                                ...response.data.BEDesignerResult.Value.ContentCodes.map(elem => {
                                    elem['level'] = saveLevel;
                                    return elem;
                                })
                            ];
                        }
                    },
                    () => upperLevelArray
                );
            });
        }
        // List is now accumulated in mainPromise
        return mainPromise.then(themeList => {
            //Switching logic
            if (swtichToCode !== undefined) {
                if (category === 'SBEM') {
                    defaultContentSbem = {
                        ...defaultContentSbem,
                        level: saveLevel,
                        code: swtichToCode
                    };
                } else {
                    defaultContent = {
                        ...defaultContent,
                        level: saveLevel,
                        code: swtichToCode
                    };
                }
            }
            
            // Choose the appropriate default based on category
            const activeDefault = category === 'SBEM' ? defaultContentSbem : defaultContent;
            
            return (activeDefault.level === SAVE_LEVEL.SYSTEM
                ? (activeDefault.code === 'systemdefault-sbem' 
                    ? ThemeService.systemdefaultSbem() 
                    : ThemeService.systemdefault())
                : ThemeService.get(chainId, hotelId, activeDefault.code, activeDefault.level, category).then(response => {
                    return response.data.BEDesignerResult.Value.Data;
                })
            ).catch(() => {
                // TODO: possible error handling here
                return activeDefault.code === 'systemdefault-sbem' 
                    ? ThemeService.systemdefaultSbem() 
                    : ThemeService.systemdefault();
            })
            .then(config => {
                if (!dontMarkAsDefault) {
                    store.dispatch({ type: ActionTypes.ADD_THEME_LIST, themeList, defaultContent, defaultContentSbem });
                } else {
                    store.dispatch({ type: ActionTypes.ADD_THEME_LIST, themeList, defaultContent: false });
                }
                
                // Dispatch the appropriate action based on category
                if (category === 'SBEM') {
                    store.dispatch({ type: ActionTypes.LOGIN_SET_IN_CONTEXT_THEME, theme: config, code: activeDefault.code });
                } else {
                    store.dispatch({ type: ActionTypes.LOGIN_SET_THEME, theme: config, code: activeDefault.code });
                    store.dispatch({ type: CALCULATE_FONT_USAGE });
                }
                
                return config;
            });
        });
    }
}

ThemeService.update = newOrUpdate.bind(null, 'update');
ThemeService.new = newOrUpdate.bind(null, 'new');

// SBEM specific methods
ThemeService.updateSbem = (chainId, hotelId, themeCode, content, saveLevel = SAVE_LEVEL.HOTEL) =>
    newOrUpdate('update', chainId, hotelId, themeCode, content, saveLevel, 'SBEM');
ThemeService.newSbem = (chainId, hotelId, themeCode, content, saveLevel = SAVE_LEVEL.HOTEL) =>
    newOrUpdate('new', chainId, hotelId, themeCode, content, saveLevel, 'SBEM');

ThemeService.contenttype = 'theme';



// WEBPACK FOOTER //
// ./src/services/ThemeService.js